import React, { useMemo } from 'react';
import { IItemForGetShortInfo } from 'types';
import { useTranslation } from 'react-i18next';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import style from './Info.module.scss';

interface IProps {
	customerName: string;
	fetchedRestaurants: IItemForGetShortInfo[];
	IDSelectedRestaurantOnRestPropsPage: Nullable<string>;
	price: Nullable<number>;
	hasOpenPrice: boolean;
	openPrice: Nullable<number>;
	openPriceMaxThreshold: Nullable<number>;
	taxPercent: Nullable<number>;
	inStop: boolean;
	isCommentVisible: boolean;
	isFiscalized: boolean;
	isTaxPercentProtectedFromSync: boolean;
	activationSchedule: string;
	deactivationSchedule: string;
	hasPortions: boolean;
}

export const Info: React.FC<IProps> = React.memo(
	({
		customerName,
		fetchedRestaurants,
		IDSelectedRestaurantOnRestPropsPage,
		price,
		hasOpenPrice,
		openPrice,
		openPriceMaxThreshold,
		taxPercent,
		inStop,
		isCommentVisible,
		isFiscalized,
		isTaxPercentProtectedFromSync,
		activationSchedule,
		deactivationSchedule,
		hasPortions,
	}) => {
		const { t } = useTranslation('form');
		const hasOpenPriceValue = hasOpenPrice ? t('yes') : t('no');
		const inStopValue = inStop ? t('yes') : t('no');
		const isCommentVisibleValue = isCommentVisible ? t('yes') : t('no');
		const isFiscalizedValue = isFiscalized ? t('yes') : t('no');
		const isTaxPercentProtectedFromSyncValue = isTaxPercentProtectedFromSync ? t('yes') : t('no');

		const restaurantName = useMemo(() => {
			return (
				fetchedRestaurants.find((restaurant) => restaurant.id === IDSelectedRestaurantOnRestPropsPage)?.name ||
				'-'
			);
		}, [fetchedRestaurants, IDSelectedRestaurantOnRestPropsPage]);

		return (
			<div className={style.container}>
				<div className={style.item}>
					<FullInfoField
						containerClassName={style.input}
						label="customerLabel"
						value={customerName}
						useInForm={true}
					/>
					<FullInfoField
						containerClassName={style.input}
						label="restaurantLabel"
						value={restaurantName}
						useInForm={true}
					/>
				</div>
				<div className={style.item}>
					{!hasPortions && (
						<FullInfoField
							containerClassName={style.input}
							label="signedPriceLabel"
							value={price}
							useInForm={true}
						/>
					)}
					<FullInfoField
						containerClassName={style.input}
						label="openPriceLabel"
						value={openPrice}
						useInForm={true}
					/>
					<FullInfoField
						containerClassName={style.input}
						label="openPriceMaxThresholdLabel"
						value={openPriceMaxThreshold}
						useInForm={true}
					/>
					<FullInfoField
						containerClassName={style.input}
						label="hasOpenPricePercentLabel"
						value={hasOpenPriceValue}
						prefixIcon="asCheckbox"
						prefixIconName={hasOpenPrice ? 'checkMark' : 'crossMark'}
						prefixIconIsChecked={hasOpenPrice}
						useInForm={true}
					/>
					<FullInfoField
						containerClassName={style.input}
						label="taxRateLabel"
						value={taxPercent}
						useInForm={true}
					/>
				</div>
				<div className={style.item}>
					<FullInfoField
						containerClassName={style.input}
						label="activationScheduleLabel"
						value={activationSchedule}
						useInForm={true}
					/>
					<FullInfoField
						containerClassName={style.input}
						label="deactivationScheduleLabel"
						value={deactivationSchedule}
						useInForm={true}
					/>
				</div>
				<div className={style.item}>
					<FullInfoField
						containerClassName={style.input}
						label="inStopLabel"
						value={inStopValue}
						prefixIcon="asCheckbox"
						prefixIconName={inStop ? 'checkMark' : 'crossMark'}
						prefixIconIsChecked={inStop}
						useInForm={true}
					/>
					<FullInfoField
						containerClassName={style.input}
						label="commentsLabel"
						value={isCommentVisibleValue}
						prefixIcon="asCheckbox"
						prefixIconName={isCommentVisible ? 'checkMark' : 'crossMark'}
						prefixIconIsChecked={isCommentVisible}
						useInForm={true}
					/>
					<FullInfoField
						containerClassName={style.input}
						label="isFiscalizedLabel"
						value={isFiscalizedValue}
						prefixIcon="asCheckbox"
						prefixIconName={isFiscalized ? 'checkMark' : 'crossMark'}
						prefixIconIsChecked={isFiscalized}
						useInForm={true}
					/>
					<FullInfoField
						containerClassName={style.input}
						label="protectFromSyncLabel"
						value={isTaxPercentProtectedFromSyncValue}
						prefixIcon="asCheckbox"
						prefixIconName={isTaxPercentProtectedFromSync ? 'checkMark' : 'crossMark'}
						prefixIconIsChecked={isTaxPercentProtectedFromSync}
						useInForm={true}
					/>
				</div>
			</div>
		);
	},
);
